import type { Current, HourPoint } from '../../../shared/contract'
import { compass, fmtHour, fmtPercent, fmtPrecip, fmtTime, fmtWind, localDate, pressure } from '../../../shared/units'
import type { V5Data, V5Frame } from '../../data/types'
import type { WxGlyphCode } from '../../icons/codes'

export const INSTRUMENTS = [
  { key: 'wind', label: 'Wind', metric: 'wind' },
  { key: 'uv', label: 'UV index', metric: 'uv' },
  { key: 'humidity', label: 'Humidity', metric: 'humidity' },
  { key: 'pressure', label: 'Pressure', metric: 'pressure' },
  { key: 'visibility', label: 'Visibility', metric: 'visibility' },
  { key: 'feels', label: 'Feels like', metric: 'feels' },
  { key: 'aqi', label: 'Air quality', metric: 'aqi' },
  { key: 'precipitation', label: 'Precipitation', metric: 'precipitation' },
] as const
export type InstrumentKey = typeof INSTRUMENTS[number]['key']
export type Severity = 'missing' | 'quiet' | 'notice' | 'high'
export interface InstrumentReading { value: string; note: string; severity: Severity; score: number }
export interface ConditionsReading {
  instruments: Record<InstrumentKey, InstrumentReading>
  stamp: string
  priority: string
  windKnown: boolean
  trendKnown: boolean
  feelsKnown: boolean
  windTransform: string
  uvTransform: string
  humidityTransform: string
  visibilityTransform: string
  feelsTransform: string
  pressureTransform: string
  sunTransform: string
  sunVisible: boolean
  sunrise: string
  sunset: string
  solarNote: string
  moonGlyph: WxGlyphCode
  moonName: string
  moonNote: string
  time: string
}

const HOUR = 3_600_000
const finite = (v: number | null | undefined): v is number => v != null && Number.isFinite(v)
const clamp = (v: number, min = 0, max = 1) => Math.max(min, Math.min(max, v))
const mark = (value: string, note: string, score: number, known: boolean): InstrumentReading => ({ value: known ? value : '—', note, score: known ? score : 0, severity: !known ? 'missing' : score >= 70 ? 'high' : score >= 30 ? 'notice' : 'quiet' })
const MOONS: readonly [WxGlyphCode, string][] = [
  ['moon-new', 'New moon'], ['moon-waxing-crescent', 'Waxing crescent'],
  ['moon-first-quarter', 'First quarter'], ['moon-waxing-gibbous', 'Waxing gibbous'],
  ['moon-full', 'Full moon'], ['moon-waning-gibbous', 'Waning gibbous'],
  ['moon-last-quarter', 'Last quarter'], ['moon-waning-crescent', 'Waning crescent'],
]

export function aqiCategory(aqi: number): string {
  return aqi <= 50 ? 'Good' : aqi <= 100 ? 'Moderate' : aqi <= 150 ? 'Sensitive groups' : aqi <= 200 ? 'Unhealthy' : aqi <= 300 ? 'Very unhealthy' : 'Hazardous'
}

/** A complete 24-hour window only. A missing hour/value is not a zero-rain hour. */
export function next24Total(hours: readonly HourPoint[], start: number): number | null {
  let total = 0
  for (let i = 0; i < 24; i++) {
    const h = hours[start + i]
    if (!h || Date.parse(h.time) !== Date.parse(hours[start].time) + i * HOUR || !finite(h.precip_mm) || h.precip_mm < 0) return null
    total += h.precip_mm
  }
  return total
}

export function emptyConditions(pending = true): ConditionsReading {
  const note = pending ? 'Waiting for the report' : 'Not supplied'
  return {
    instruments: Object.fromEntries(INSTRUMENTS.map(({ key }) => [key, mark('—', note, 0, false)])) as ConditionsReading['instruments'],
    stamp: pending ? 'Waiting for conditions' : 'Conditions unavailable', priority: 'Missing readings are unknown, never zero.',
    windKnown: false, trendKnown: false, feelsKnown: false, windTransform: 'rotate(0deg)', uvTransform: 'translateX(0px)', humidityTransform: 'translateX(0px)',
    visibilityTransform: 'scaleX(0)', feelsTransform: 'translateX(0px)', pressureTransform: 'rotate(0deg)',
    sunTransform: 'translate(0px, 0px)', sunVisible: false, sunrise: '—', sunset: '—', solarNote: 'Sun times not supplied',
    moonGlyph: 'unknown', moonName: 'Moon unavailable', moonNote: 'Phase not supplied', time: '',
  }
}

function uvWindow(data: V5Data, date: string): (instant: number) => string {
  const report = data.report!
  const hours = report.hourly.filter((h) => localDate(h.time, report.location.timezone) === date)
  const exposed = hours.filter((h) => finite(h.uv_index) && h.uv_index >= 3)
  if (!exposed.length) return () => hours.some((h) => !finite(h.uv_index)) || !hours.length ? 'UV outlook incomplete' : 'Low UV in the available forecast'
  const runs: [number, number][] = []
  for (const hour of exposed) {
    const t = Date.parse(hour.time)
    const last = runs.at(-1)
    if (last && last[1] === t) last[1] = t + HOUR
    else runs.push([t, t + HOUR])
  }
  const time = (t: number) => fmtHour(new Date(t).toISOString(), report.location.timezone, data.units.clock)
  return (instant) => {
    const run = runs.find(([, end]) => end > instant) ?? runs.at(-1)!
    return `${instant >= run[1] ? 'UV 3+ was' : 'Protect'} ${time(run[0])}–${time(run[1])}`
  }
}

function makeReading(data: V5Data, frame: V5Frame, sample: Current | HourPoint, now: boolean, windows: Map<string, (instant: number) => string>, hoursByTime: Map<number, HourPoint>, hourIndex: Map<number, number>): ConditionsReading {
  const report = data.report!
  const { units } = data
  const { timezone } = report.location
  const date = localDate(new Date(frame.time).toISOString(), timezone)
  const air = now ? report.air_quality : null
  const aqi = now ? air?.aqi : (sample as HourPoint).aqi
  const gust = sample.wind_gust_ms
  const dir = sample.wind_dir_deg
  const speed = sample.wind_ms
  const uv = sample.uv_index
  const dew = sample.dewpoint_c
  const vis = sample.visibility_m
  const apparent = sample.apparent_c
  const actual = sample.temperature_c
  const delta = finite(apparent) && finite(actual) ? apparent - actual : null
  const comfort = !finite(dew) ? 'comfort unknown' : dew < 0 ? 'dry air' : dew <= 13 ? 'comfortable' : dew <= 18 ? 'a little humid' : dew <= 21 ? 'muggy' : 'very humid'
  const before = hoursByTime.get(frame.time - 3 * HOUR)?.pressure_hpa
  const change = now ? report.current.pressure_change_3h_hpa : finite(sample.pressure_hpa) && finite(before) ? sample.pressure_hpa - before : null
  const trend = now ? report.current.pressure_trend : !finite(change) ? null : change > 0.5 ? 'rising' : change < -0.5 ? 'falling' : 'steady'
  const trendText = trend ? trend[0].toUpperCase() + trend.slice(1) : 'Change not known'
  const pressureDelta = finite(change) ? pressure(Math.abs(change), units.pressure) : null
  const pressureNote = `${trendText}${pressureDelta == null ? '' : ` · ${change! >= 0 ? '+' : '−'}${pressureDelta.toFixed(units.pressure === 'inHg' ? 2 : 1)} ${units.pressure} / 3 h`}`
  const direction = finite(dir) ? compass(dir) : '—'
  const pollution = air?.dominant_pollutant?.replace(/pm2_5/i, 'PM2.5').replace(/pm10/i, 'PM10').replace(/o3/i, 'O₃')
  const start = hourIndex.get(frame.time)
  const rain = now ? report.precipitation.next_24h_mm : start == null ? null : next24Total(report.hourly, start)
  const rainNote = now ? `Next 24 h · past ${fmtPrecip(report.precipitation.past_24h_mm, units.precip)}` : `Next 24 h · this hour ${fmtPrecip((sample as HourPoint).precip_mm, units.precip)}`
  const instruments: ConditionsReading['instruments'] = {
    wind: mark(frame.wind, `Gusts ${fmtWind(gust, units.wind)} · from ${direction}`, Math.max(finite(speed) ? speed * 2.5 : 0, finite(gust) ? gust * 2.4 : 0), finite(speed)),
    uv: mark(frame.uv, windows.get(date)?.(frame.time) ?? 'Protection time unavailable', finite(uv) ? uv >= 11 ? 95 : uv >= 8 ? 80 : uv >= 6 ? 65 : uv >= 3 ? 35 : 0 : 0, finite(uv)),
    humidity: mark(frame.humidity, `Dew ${frame.dew} · ${comfort}`, finite(dew) && dew > 21 ? 45 : 0, finite(sample.humidity_pct)),
    pressure: mark(frame.pressure, pressureNote, finite(change) && change < -4 ? 35 : 0, finite(sample.pressure_hpa)),
    visibility: mark(frame.visibility, !finite(vis) ? 'Not supplied for this time' : vis < 1000 ? 'Very low visibility' : vis < 5000 ? 'Reduced visibility' : 'Good visibility', finite(vis) ? vis < 1000 ? 95 : vis < 5000 ? 50 : 0 : 0, finite(vis)),
    feels: mark(frame.feels, `Actual ${frame.temperature}${delta == null ? '' : ` · ${Math.abs(delta) < 1 ? 'similar' : delta > 0 ? 'feels warmer' : 'feels colder'}`}`, finite(apparent) ? apparent > 40 || apparent < -20 ? 85 : apparent > 32 || apparent < -10 ? 45 : 0 : 0, finite(apparent)),
    aqi: mark(finite(aqi) ? String(Math.round(aqi)) : '—', !finite(aqi) ? 'AQI not supplied for this time' : `${aqiCategory(aqi)} · ${pollution ?? 'pollutant unknown'}`, finite(aqi) ? aqi > 200 ? 100 : aqi > 150 ? 95 : aqi > 100 ? 75 : aqi > 50 ? 30 : 0 : 0, finite(aqi)),
    precipitation: mark(fmtPrecip(rain, units.precip), rainNote, finite(rain) ? rain >= 50 ? 80 : rain >= 25 ? 45 : 0 : 0, finite(rain)),
  }
  if (!finite(uv)) instruments.uv.note = 'UV and protection time unavailable'
  if (!finite(sample.humidity_pct) && !finite(dew)) instruments.humidity.note = 'Humidity and dew point unavailable'
  if (!finite(rain)) instruments.precipitation.note = now ? 'Next 24 h total not supplied' : 'Incomplete 24 h forecast window'
  const ranked = INSTRUMENTS.filter(({ key }) => instruments[key].score >= 30).sort((a, b) => instruments[b.key].score - instruments[a.key].score)
  const day = report.daily.find((d) => d.date === date)
  const sunrise = now ? report.astronomy?.sunrise : day?.sunrise
  const sunset = now ? report.astronomy?.sunset : day?.sunset
  const rise = sunrise ? Date.parse(sunrise) : NaN
  const set = sunset ? Date.parse(sunset) : NaN
  const solarKnown = Number.isFinite(rise) && Number.isFinite(set) && set > rise
  const progress = solarKnown ? clamp((frame.time - rise) / (set - rise)) : 0
  const phase = now ? report.astronomy?.moon?.phase : day?.moon_phase
  const illumination = now ? report.astronomy?.moon?.illumination : day?.moon_illumination
  const moon = finite(phase) && phase >= 0 && phase <= 1 ? MOONS[Math.round(phase * 8) % 8] : null
  return {
    instruments, windKnown: finite(dir), trendKnown: trend != null, feelsKnown: delta != null, windTransform: `rotate(${finite(dir) ? dir : 0}deg)`,
    uvTransform: `translateX(${finite(uv) ? clamp(uv / 12) * 100 : 0}px)`,
    humidityTransform: `translateX(${finite(sample.humidity_pct) ? clamp(sample.humidity_pct / 100) * 100 : 0}px)`,
    visibilityTransform: `scaleX(${finite(vis) ? clamp(vis / 16000) : 0})`,
    feelsTransform: `translateX(${delta == null ? 0 : clamp(delta, -10, 10) * 4}px)`,
    pressureTransform: `rotate(${trend === 'rising' ? -25 : trend === 'falling' ? 25 : 0}deg)`,
    sunTransform: `translate(${14 + 172 * progress}px, ${54 - 168 * progress * (1 - progress)}px)`,
    sunVisible: solarKnown && frame.time >= rise && frame.time <= set,
    sunrise: solarKnown ? fmtTime(sunrise, timezone, units.clock) : '—', sunset: solarKnown ? fmtTime(sunset, timezone, units.clock) : '—',
    solarNote: solarKnown ? frame.time < rise ? 'Before sunrise' : frame.time > set ? 'After sunset' : 'Through the daylight' : 'Sun times not supplied',
    moonGlyph: moon?.[0] ?? 'unknown', moonName: moon?.[1] ?? 'Moon unavailable',
    moonNote: moon ? `${finite(illumination) ? `${fmtPercent(illumination * 100)} illuminated` : 'Illumination unknown'}` : 'Phase not supplied for this date',
    stamp: `${data.offline ? 'Offline · ' : ''}${data.error ? 'Refresh failed · ' : ''}${now ? 'As of' : 'Forecast for'} ${fmtTime(new Date(frame.time).toISOString(), timezone, units.clock)}`,
    priority: ranked.length ? `Watch ${ranked.slice(0, 2).map(({ label }) => label.replace(/^(?!UV)\w/, (c) => c.toLowerCase())).join(' & ')}` : 'Your local conditions',
    time: String(frame.time),
  }
}

/** All formatting, ranking and instrument geometry is prepared once, outside cursor input. */
const prepared = new WeakMap<readonly V5Frame[], { data: V5Data; minute: number; readings: Map<V5Frame, ConditionsReading> }>()
export function prepareConditions(data: V5Data): Map<V5Frame, ConditionsReading> {
  const minute = data.mock ? 0 : Math.floor(Date.now() / 60_000)
  const hit = prepared.get(data.frames)
  if (hit && hit.minute === minute && hit.data.report === data.report && hit.data.units === data.units && hit.data.offline === data.offline && hit.data.error === data.error) return hit.readings
  const readings = new Map<V5Frame, ConditionsReading>()
  if (!data.report) return readings
  const windows = new Map<string, (instant: number) => string>()
  const hoursByTime = new Map([...(data.report.earlier_today ?? []), ...data.report.hourly].map((h) => [Date.parse(h.time), h]))
  const hourIndex = new Map(data.report.hourly.map((h, i) => [Date.parse(h.time), i]))
  for (const date of new Set([data.report.current.time, ...data.frames.map((f) => new Date(f.time).toISOString())].map((time) => localDate(time, data.report!.location.timezone)))) windows.set(date, uvWindow(data, date))
  if (data.now) readings.set(data.now, makeReading(data, data.now, data.report.current, true, windows, hoursByTime, hourIndex))
  for (const frame of data.frames) {
    const sample = data.models?.hours[frame.index]?.hour
    if (sample) readings.set(frame, makeReading(data, frame, sample, false, windows, hoursByTime, hourIndex))
  }
  prepared.set(data.frames, { data, minute, readings })
  return readings
}
